/**
 * useTutorLiveFeedProposals — « <champion> propose » : une activité déjà générée sur un signal du live, que le tuteur
 * voit au centre ou envoie d'un geste (Wajih, 04/11 : « le pont vers la cible », où le champion écoute la séance et
 * propose de lui-même ; l'agent qui lira les moments du live est un suivi).
 *
 * Le signal d'aujourd'hui (arbitrage 5) : **un rendu qui attend la validation** (`exercise_done`). Le champion
 * prépare la première marche de l'exercice rendu (`generatePropositionFirstSubExercise`, l'endpoint de génération
 * d'aujourd'hui, que le champ ne lance jamais : leurs réponses ne se croisent pas) : si l'élève a buté, elle est
 * prête. La proposition vit tant que le rendu attend ; envoyée ou écartée, elle se range.
 *
 * **Bornée** (`proposalAllowed`, pur et testé) : une à la fois par élève, une par élève toutes les 10 minutes,
 * jamais pendant que le tuteur génère. **Mesurée** : chaque appel se compte dans les traces de la salle
 * (`traceHelperProposal`).
 *
 * « Envoyer » crée l'activité et la montre (`createActivityAsTutor`, puis `newTodo` avec `forceFocus`, comme
 * « Montrer à l'élève ») ; il attend qu'aucune création du champ ne soit en vol, qui lit la même tranche.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useDispatch } from "react-redux"

import { activitiesService } from "../../../../../../data/services/activitiesService"
import { apiCall } from "../../../../../../engine/shared/apiCall"
import { type EngineTelemetry } from "../../../../../../engine/shared/telemetry"
import { traceHelperProposal } from "../../../../../../engine/live/tutor/helperProposalTraces"
import { type TutorReviewStageProps } from "../../../../../../design-system/organisms/TutorReviewStage"
import { type UIGeneratedActivity } from "../../../types"
import {
    createResultBody,
    toUIGeneratedActivity,
    type ApiGeneratedMaterial,
} from "../../../../adapters/parent/Activities/activityGenerationAdapter"
import { type ApiStudentLists } from "../../../../adapters/tutor/Live/tutorLiveFeedAdapter"
import { proposalAllowed } from "../../../../view-models/tutor/Live/tutorLiveGenerationRules"
import {
    type TutorLiveCard,
    type TutorLiveTransientEntry,
} from "../../../../view-models/tutor/Live/tutorLiveFeedViewModel"
import { rawId } from "./tutorLiveContract"

const TEXTS = {
    reason: (firstname: string, title: string | null) =>
        `${firstname} a rendu ${title != null ? `« ${title} »` : "son exercice"}. Si ça a coincé, une marche plus bas est prête.`,
    meta: "Prête · la première marche",
    validWhile: "Tant que ce rendu attend ta validation",
    send: (firstname: string) => `Envoyer à ${firstname}`,
    sent: (firstname: string) => `Activité envoyée à ${firstname}`,
    failed: "L'envoi n'a pas abouti. La proposition reste là.",
    wait: "Une activité part déjà vers l'élève : réessaie dans un instant.",
    heading: "La proposition du champion",
}

type Proposal = {
    key: string
    studentId: string
    activityId: string
    startedAt: number
    status: "pending" | "ready" | "error"
    /** Le titre de l'exercice rendu, que la carte cite (pas celui de la proposition). */
    sourceTitle: string | null
    material?: ApiGeneratedMaterial
    activity?: UIGeneratedActivity
    send: "idle" | "sending" | "sent"
    dismissed?: boolean
}

export type TutorLiveFeedProposalsDeps = {
    /** Les cartes vivantes du fil : un rendu qui attend sa validation fait naître une proposition. */
    cards: TutorLiveCard[]
    accountStudents: (ApiStudentLists & { id?: string | number })[]
    firstnameOf: (studentId: string) => string
    send:
        ((type: string, data: Record<string, unknown>, to: number[] | null, policy: "keep" | "drop") => unknown) | null
    telemetry: EngineTelemetry
    /** Le tuteur génère dans le champ : aucune proposition ne part d'avance. */
    tutorGenerating: boolean
    /** Une proposition du champ se crée : « Envoyer » attend (la même réponse), et le dit. */
    tutorCreating: boolean
    /** Une présentation tourne : la relecture se pose sur ce que voient les élèves, elle le dit. */
    presenting: boolean
    quizAvailable: boolean
    notify: (message: string, tone: "success" | "danger" | "info") => void
    helperName: string
}

export type TutorLiveFeedProposals = {
    /** Les cartes « <champion> propose » du fil, chacune pour son élève. */
    transient: TutorLiveTransientEntry[]
    /** « Voir », « Envoyer », « Écarter » d'une carte (son id d'entrée). */
    onAction: (entryId: string, action: "see" | "send" | "dismiss") => boolean
    /** La proposition vue au centre (« Voir »), ou `null`. */
    review: TutorReviewStageProps | null
    /** Une proposition part (sa création attend sa réponse) : le champ attend pour créer les siennes. */
    sending: boolean
}

const ENTRY_PREFIX = "proposal-"

/** La première marche de l'exercice rendu : le corps des propositions d'aujourd'hui (`num_clusters`, la cible). */
function firstStepBody(studentId: string, level: unknown, duration: unknown): Record<string, unknown> {
    const body: Record<string, unknown> = { num_clusters: 1, student_ids: [rawId(studentId)] }
    const targetLevel = parseInt(String(level ?? ""), 10)
    const targetDuration = parseInt(String(duration ?? ""), 10)
    if (targetLevel) body.target_level = targetLevel
    if (targetDuration) body.duration = targetDuration
    return body
}

type RawActivity = {
    id?: string | number
    estimated_duration?: number | null
    material?: { id?: string | number | null; title?: string | null; level?: string | number | null } | null
}

export function useTutorLiveFeedProposals(deps: TutorLiveFeedProposalsDeps): TutorLiveFeedProposals {
    const dispatch = useDispatch()
    const depsRef = useRef(deps)
    depsRef.current = deps
    const [proposals, setProposals] = useState<Record<string, Proposal>>({})
    const proposalsRef = useRef(proposals)
    proposalsRef.current = proposals
    const lastStartedAt = useRef<Record<string, number>>({})
    const requested = useRef(0)
    const [seeing, setSeeing] = useState<string | null>(null)

    // Les rendus qui attendent leur validation : chacun peut faire naître sa proposition.
    const submissions = useMemo(
        () =>
            deps.cards
                .filter((card) => card.signal === "exercise_done" && card.activityId != null)
                .map((card) => ({ studentId: card.studentId, activityId: card.activityId as string })),
        [deps.cards]
    )
    const submissionsKey = submissions.map((s) => `${s.studentId}:${s.activityId}`).join("|")

    const update = useCallback((key: string, patch: Partial<Proposal>) => {
        setProposals((current) =>
            current[key] != null ? { ...current, [key]: { ...current[key], ...patch } } : current
        )
    }, [])

    useEffect(() => {
        const d = depsRef.current
        const now = Date.now()
        for (const submission of submissions) {
            const key = `${submission.studentId}:${submission.activityId}`
            if (proposalsRef.current[key] != null) continue
            const inFlight = new Set(
                Object.values(proposalsRef.current)
                    .filter((proposal) => proposal.status === "pending")
                    .map((proposal) => proposal.studentId)
            )
            if (
                !proposalAllowed({
                    studentId: submission.studentId,
                    now,
                    inFlight,
                    lastStartedAt: lastStartedAt.current,
                    tutorGenerating: d.tutorGenerating,
                })
            )
                continue
            const student = d.accountStudents.find((candidate) => String(candidate?.id) === submission.studentId)
            const raw = [...(student?.activities ?? []), ...(student?.todo_activities ?? [])].find(
                (activity) => String(activity.id) === submission.activityId
            ) as RawActivity | undefined
            const materialId = raw?.material?.id
            if (materialId == null) continue
            lastStartedAt.current[submission.studentId] = now
            requested.current += 1
            const proposal: Proposal = {
                key,
                studentId: submission.studentId,
                activityId: submission.activityId,
                startedAt: now,
                status: "pending",
                sourceTitle: raw?.material?.title ?? null,
                send: "idle",
            }
            proposalsRef.current = { ...proposalsRef.current, [key]: proposal }
            setProposals(proposalsRef.current)
            traceHelperProposal(d.telemetry, "requested", { count: requested.current, signal: "exercise_done" })
            apiCall<ApiGeneratedMaterial | { data?: ApiGeneratedMaterial }>(
                dispatch as never,
                activitiesService.generatePropositionFirstSubExercise,
                {
                    routeParams: { materialId },
                    data: firstStepBody(submission.studentId, raw?.material?.level, raw?.estimated_duration),
                }
            )
                .then((response) => {
                    const material = ((response as { data?: ApiGeneratedMaterial })?.data ??
                        response) as ApiGeneratedMaterial
                    update(key, {
                        status: "ready",
                        material,
                        activity: { ...toUIGeneratedActivity(material), id: key, status: "ready" },
                    })
                    traceHelperProposal(depsRef.current.telemetry, "ready", { durationMs: Date.now() - now })
                })
                .catch(() => {
                    update(key, { status: "error" })
                    traceHelperProposal(depsRef.current.telemetry, "failed", { durationMs: Date.now() - now })
                })
        }
        // `submissionsKey` dit quand les rendus changent ; le reste se lit par refs.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submissionsKey, deps.tutorGenerating, dispatch, update])

    const live = new Set(submissions.map((s) => `${s.studentId}:${s.activityId}`))
    // Une proposition vit tant que son rendu attend, ou qu'elle part ; envoyée, elle reste une fois (son accusé).
    const shown = Object.values(proposals).filter(
        (proposal) =>
            proposal.dismissed !== true &&
            proposal.status === "ready" &&
            (live.has(proposal.key) || proposal.send !== "idle")
    )

    const transient = useMemo<TutorLiveTransientEntry[]>(
        () =>
            shown.map((proposal) => {
                const firstname = deps.firstnameOf(proposal.studentId)
                const iso = new Date(proposal.startedAt).toISOString()
                return {
                    studentId: proposal.studentId,
                    at: proposal.startedAt,
                    entry: {
                        kind: "proposal",
                        id: `${ENTRY_PREFIX}${proposal.key}`,
                        at: iso,
                        reason: TEXTS.reason(firstname, proposal.sourceTitle),
                        title: proposal.activity?.title ?? "",
                        meta: TEXTS.meta,
                        validWhile: proposal.send === "idle" ? TEXTS.validWhile : undefined,
                        state: proposal.send === "idle" ? "ready" : proposal.send,
                        sendLabel: TEXTS.send(firstname),
                    },
                }
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [shown.map((p) => `${p.key}:${p.send}:${p.status}`).join("|"), deps.firstnameOf]
    )

    const sendProposal = useCallback(
        (key: string) => {
            const d = depsRef.current
            const proposal = proposalsRef.current[key]
            if (proposal?.material == null || proposal.send !== "idle") return
            // Une création du champ attend sa réponse sur la même tranche : la proposition attend son tour, et le dit.
            if (d.tutorCreating) {
                d.notify(TEXTS.wait, "info")
                return
            }
            update(key, { send: "sending" })
            const firstname = d.firstnameOf(proposal.studentId)
            apiCall<{ id?: string | number }>(dispatch as never, activitiesService.createActivityAsTutor, {
                routeParams: { id: proposal.studentId },
                data: {
                    ...createResultBody(proposal.material),
                    ...(d.quizAvailable ? { has_prerequisite_quiz: true } : {}),
                },
            })
                .then((activity) => {
                    const to = [Number(proposal.studentId)]
                    const id = rawId(proposal.studentId)
                    // Comme « Montrer à l'élève » : l'élève est prévenu, puis l'activité lui est montrée.
                    d.send?.("newTodo", { id, activity }, to, "drop")
                    d.send?.("newTodo", { id, activity, forceFocus: true }, to, "drop")
                    update(key, { send: "sent" })
                    d.notify(TEXTS.sent(firstname), "success")
                    traceHelperProposal(d.telemetry, "sent", {})
                    setSeeing((current) => (current === key ? null : current))
                })
                .catch(() => {
                    update(key, { send: "idle" })
                    d.notify(TEXTS.failed, "danger")
                })
        },
        [dispatch, update]
    )

    const onAction = useCallback(
        (entryId: string, action: "see" | "send" | "dismiss") => {
            if (!entryId.startsWith(ENTRY_PREFIX)) return false
            const key = entryId.slice(ENTRY_PREFIX.length)
            if (action === "see") setSeeing(key)
            if (action === "send") sendProposal(key)
            if (action === "dismiss") {
                update(key, { dismissed: true })
                traceHelperProposal(depsRef.current.telemetry, "dismissed", {})
            }
            return true
        },
        [sendProposal, update]
    )

    const seen = seeing != null ? proposals[seeing] : undefined
    const review: TutorReviewStageProps | null =
        seen?.activity != null
            ? {
                  open: true,
                  studentName: deps.firstnameOf(seen.studentId),
                  heading: TEXTS.heading,
                  proposals: [
                      {
                          ...seen.activity,
                          creation: seen.send === "sent" ? "created" : seen.send === "sending" ? "creating" : undefined,
                      },
                  ],
                  index: 0,
                  // Ni « Ajouter sans montrer » ni « Régénérer » : la proposition est faite pour être montrée.
                  onSend: () => sendProposal(seen.key),
                  onClose: () => setSeeing(null),
                  sentId: seen.send === "sent" ? seen.key : null,
                  writingLabel: deps.helperName,
                  presenting: deps.presenting,
              }
            : null

    const sending = Object.values(proposals).some((proposal) => proposal.send === "sending")
    return { transient, onAction, review, sending }
}
